import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b]">
        <Loader2 className="animate-spin text-[#c5a059]" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back, Admin.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed. Please check your credentials.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0b] border border-[#2a2a2d] rounded-md px-4 py-3 text-sm text-white placeholder:text-[#6f6f74] focus:border-[#c5a059] focus:outline-none transition";

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative w-full max-w-md bg-[#0f0f11] border border-[#2a2a2d] rounded-2xl p-10 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo_mark.png" alt="Aryavarta" className="h-12 w-12 object-contain" />
          <div>
            <p className="font-serif text-xl leading-tight">Aryavarta Admin</p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8f8f94]">Internal Access</p>
          </div>
        </div>

        <h1 className="font-serif text-3xl">Sign in to continue.</h1>
        <p className="text-sm text-[#8f8f94] mt-2">Manage plots, layouts and incoming enquiries.</p>

        <form className="mt-8 space-y-4" onSubmit={submit}>
          <input
            data-testid="admin-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            required
            autoComplete="username"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            required
            autoComplete="current-password"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-gold w-full py-3 rounded-md text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <Lock size={14} />}
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
